<svelte:options
  customElement={{
    tag: "oc-skip-link-v1",
    /*                       */
    extend: window.__components.extend(),
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import { focusDeepWithin } from "@otto-ec/otto-components-utils/utils/focus/focusDeepWithin";
  import { focusNextElement } from "./focusNextElement";
  import { getNextParentElement } from "../../../common/utils/getNextParentElement";
  import { track } from "../../../common/utils/track";

  const Host = $host();

  let hasFocus = $state(false);
  let isAnimating = $state(false);

  let isMounted = $state(false); /*                                */
  onMount(() => (isMounted = true));

  let maxWidth = $derived.by(() => {
    track(isMounted, hasFocus);
    const screenWidth = window.innerWidth;
    const { x } = Host.getBoundingClientRect();
    const { left } = getComputedStyle(Host);
    return `${screenWidth - x - parseInt(left)}px`;
  });

  const onclick = (ev: MouseEvent): void => {
    let children = ev
      .composedPath()
      .slice(0, ev.composedPath().indexOf(Host)) /*                                              */
      .filter((e): e is HTMLElement => e instanceof HTMLElement);

    const skipToQuery = children.map((c) => c.dataset?.ocSkipLinkV1To).find(Boolean);
    if (skipToQuery) {
      /*                                                      */
      let skipTo = document.body.querySelector<HTMLElement>(skipToQuery);
      if (skipTo) focusDeepWithin(skipTo);
      return;
    }

    const skipAfterQuery = children.map((c) => c.dataset?.ocSkipLinkV1After).find(Boolean);
    if (skipAfterQuery) {
      /*                                                                           */
      let skipAfter = document.body.querySelector<HTMLElement>(skipAfterQuery);
      if (skipAfter) focusNextElement(skipAfter);
      return;
    }

    /*                                                                  */
    let parentElement = getNextParentElement(Host);
    if (parentElement) focusNextElement(parentElement);
  };
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="skip-link"
  class:--visible={hasFocus}
  class:--animating={isAnimating}
  class:--mounted={isMounted}
  style:width={maxWidth}
  onfocusin={() => (hasFocus = true)}
  onfocusout={() => (hasFocus = false)}
  ontransitionstart={() => (isAnimating = true)}
  ontransitionend={() => (isAnimating = false)}
  {onclick}
>
  <slot />
</div>

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  :host {
    @include mixins.no-tap-highlight();
    position: absolute;
    left: tokens.$oc-component-skip-link-position-x;
    top: tokens.$oc-component-skip-link-position-y;
    z-index: 9999999999; /*                                           */
    pointer-events: none;
    width: 100vw;
    overflow: hidden;
  }

  .skip-link {
    pointer-events: all;
    display: flex;
    flex-wrap: wrap;
    box-sizing: border-box;
    padding: tokens.$oc-component-skip-link-spacing;
    gap: tokens.$oc-component-skip-link-gap;
    border-radius: tokens.$oc-component-skip-link-border-radius;
    background: tokens.$oc-component-skip-link-background-color;
    max-width: fit-content;
    opacity: 0;
  }

  .--visible {
    opacity: 1;
  }

  .skip-link:not(.--visible):not(.--animating):not(:focus-within) {
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /*               */
  @media (prefers-reduced-motion: no-preference) {
    .--mounted {
      transition: opacity tokens.$oc-component-skip-link-disappear-easing
        tokens.$oc-component-skip-link-disappear-duration;
      .--visible {
        transition: opacity tokens.$oc-component-skip-link-appear-easing
          tokens.$oc-component-skip-link-appear-duration;
      }
    }
  }
</style>
